import { ADMISSIONS_STATS, WHY_PILLARS } from '@/data/admissions';
import { AnimatedCounter, ScrollReveal, SplitReveal } from '@/components/motion';

/**
 * WhyIbas (/admissions — section 1) — the "Why study with IBAS" opener.
 *
 * A calm, credibility-first grid: a SplitReveal headline over a short lede,
 * the four WHY_PILLARS as ScrollReveal-staggered TiltCards (each with its
 * icon in a soft navy chip and a quiet hover lift), then an institution-wide
 * proof-figure row where each ADMISSIONS_STATS value counts up on scroll.
 * A restrained FLEXI · EDU · CARE strip closes the section — labels only.
 *
 * Motion: SplitReveal headline, ScrollReveal child stagger for the grid and
 * the figure row, per-card 3D tilt on pointer, scroll-triggered counters.
 * All primitives are reduced-motion safe and never strand content hidden.
 */
export function WhyIbas() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Why study with IBAS
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Built for career mobility and advancement.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Advancing in today’s business environment requires more than a qualification. It
              requires recognition, credibility, and the ability to operate across borders.
            </p>
          </ScrollReveal>
        </header>

        {/* ── The four pillars ────────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2"
        >
          {WHY_PILLARS.map((pillar) => (
            <div
              key={pillar.title}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <pillar.Icon aria-hidden className="size-6" />
              </div>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {pillar.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {pillar.description}
              </p>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Institution-wide proof figures ──────────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <ScrollReveal
            stagger={0.08}
            className="grid grid-cols-2 gap-8 lg:grid-cols-4"
          >
            {ADMISSIONS_STATS.map((stat) => (
              <div key={stat.label} className="text-center sm:text-left">
                <AnimatedCounter
                  value={stat.value}
                  suffix={stat.suffix}
                  className="font-display text-4xl leading-none font-semibold text-brand-navy md:text-5xl"
                />
                <p className="mt-3 text-sm leading-snug text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            ))}
          </ScrollReveal>
          <p className="mt-8 text-xs text-muted-foreground">
            Figures are institution-wide.
          </p>
        </div>

        {/* ── FLEXI · EDU · CARE — labels only ────────────────────────── */}
        <ScrollReveal
          from="up"
          delay={0.1}
          className="mt-14 grid grid-cols-1 gap-4 border-t border-border pt-10 sm:grid-cols-3"
        >
          {[
            { tag: 'Flexi', label: 'Flexible design' },
            { tag: 'Edu', label: 'Applied academic depth' },
            { tag: 'Care', label: 'Support across the journey' },
          ].map((item) => (
            <div key={item.tag} className="flex items-baseline gap-3">
              <span className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
                {item.tag}
              </span>
              <span className="font-display text-base text-foreground">{item.label}</span>
            </div>
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
}
